import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Instagram,
  Facebook,
  Youtube,
  Phone,
  Mail,
  Zap,
  Scissors,
  Home,
  TrendingUp,
  Shield,
  PhoneCall,
  Sparkles,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  useSmoothScroll,
  MaskLine,
  Reveal,
  CountUp,
  TiltCard,
  staggerContainer,
  staggerItem,
} from "@/components/motion";

const HERO_PORTRAIT = "/dom-portrait.jpg";
const ABOUT_IMG = "/dom-portrait.jpg";
const ARCH_BG =
  "https://images.unsplash.com/photo-1561518658-db43bb106aeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMGZpbmFuY2lhbHxlbnwwfHx8fDE3Nzg2OTkwMjd8MA&ixlib=rb-4.1.0&q=85";

const EASE = [0.16, 1, 0.3, 1];

const ICON_MAP = {
  zap: Zap,
  scissors: Scissors,
  home: Home,
  "trending-up": TrendingUp,
  shield: Shield,
  phone: PhoneCall,
};

const NAV = [
  ["Market", "#market", "nav-market"],
  ["Hacks", "#hacks", "nav-hacks"],
  ["Insights", "#articles", "nav-articles"],
  ["About", "#about", "nav-about"],
  ["Connect", "#connect", "nav-connect"],
];

function Header() {
  return (
    <motion.header
      initial={{ y: "-100%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: EASE }}
      className="border-b border-black sticky top-0 bg-white/90 backdrop-blur-md z-50"
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 h-16 flex items-center justify-between">
        <a href="#top" className="font-heading font-black text-lg tracking-tight" data-testid="brand-mark">
          DOM
          <span className="text-[#FF4500] inline-block animate-pulse">.</span>
          LAFRANCE
        </a>
        <nav className="hidden md:flex gap-7 font-mono text-[11px] uppercase tracking-[0.18em]">
          {NAV.map(([label, href, tid]) => (
            <a
              key={href}
              href={href}
              className="group flex items-center gap-1.5 hover:text-[#FF4500] transition-colors"
              data-testid={tid}
            >
              {label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <span className="hidden lg:flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-black/50">
            <span className="w-1.5 h-1.5 bg-[#FF4500] rounded-full animate-pulse" />
            Dryden, ON · Open for advising
          </span>
          <Link
            to="/admin/login"
            className="font-mono text-[11px] uppercase tracking-[0.18em] border border-black px-3 py-2 hover:bg-black hover:text-white transition-colors"
            data-testid="header-admin-link"
          >
            Admin
          </Link>
        </div>
      </div>
    </motion.header>
  );
}

function Hero() {
  return (
    <section id="top" className="border-b border-black overflow-hidden" data-testid="hero-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 flex flex-col justify-between">
          <MaskLine delay={0.15} className="label-eyebrow">
            LICENSED FINANCIAL PROFESSIONAL
          </MaskLine>

          <h1
            className="font-heading font-black leading-[0.85] tracking-tighter mt-6 text-[12vw] sm:text-[10vw] lg:text-[7vw]"
            data-testid="hero-title"
          >
            <MaskLine delay={0.3}>DOM</MaskLine>
            <MaskLine delay={0.4}>
              LAFRANCE<span className="text-[#FF4500]">.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.55 }}
            className="mt-8 max-w-xl text-lg text-black/70 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Plain-talking market intel, honest money-saving hacks, and personal guidance on
            insurance & investments — from a Licensed Financial Professional who actually picks up the phone.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a href="#connect" className="btn-primary" data-testid="hero-cta-connect">Book a chat →</a>
            <a href="#articles" className="btn-secondary" data-testid="hero-cta-read">Read the latest</a>
          </motion.div>
        </div>

        <div className="lg:col-span-5 flex items-center justify-center">
          <div className="relative w-full">
            <div className="spotlight-bloom" />
            <TiltCard className="relative">
              <motion.div
                initial={{ clipPath: "inset(0 0 100% 0)" }}
                animate={{ clipPath: "inset(0 0 0% 0)" }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.4 }}
                className="border border-black aspect-[4/5] overflow-hidden bg-[#F5F5F5] relative"
              >
                <img
                  src={HERO_PORTRAIT}
                  alt="Dom Lafrance, Licensed Financial Professional in Dryden, Ontario"
                  width={1000}
                  height={1250}
                  fetchPriority="high"
                  className="w-full h-full object-cover object-center grayscale hover:grayscale-0 transition-all duration-700"
                  data-testid="hero-portrait"
                />
                <div className="absolute top-0 left-0 border-b border-r border-black bg-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">
                  LICENSED
                </div>
                <div className="absolute bottom-0 right-0 border-t border-l border-black bg-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]">
                  ON DUTY
                </div>
              </motion.div>
            </TiltCard>
          </div>
        </div>
      </div>
    </section>
  );
}

function Marquee({ text, phrases }) {
  const items = phrases || Array.from({ length: 10 }, () => text);
  return (
    <div className="border-y border-black bg-[#F5F5F5] overflow-hidden py-5">
      <div className="marquee-track">
        {[0, 1].map((dup) => (
          <div key={dup} className="flex gap-16 pr-16 font-mono text-sm uppercase tracking-[0.28em] text-black/70 whitespace-nowrap">
            {items.map((t, i) => (
              <span key={`${dup}-${i}`} className="inline-flex items-center gap-4">
                <span className="w-1.5 h-1.5 bg-[#FF4500]" />
                {t}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function MarketSnapshot({ market }) {
  const policyRate = market?.policy_rate ?? 3.25;
  const mortgageRate = market?.mortgage_5yr ?? 4.6;
  const asOf = market?.as_of
    ? new Date(market.as_of + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : null;
  return (
    <section id="market" className="border-b border-black" data-testid="market-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-24 md:py-32">
        <Reveal className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="label-eyebrow">MARKET SNAPSHOT</div>
            <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none mt-4">
              The market in <em className="not-italic text-[#FF4500]">plain English.</em>
            </h2>
          </div>
          <p className="max-w-md text-black/70">
            No jargon. No fearmongering. Just the few numbers that actually matter for your
            household this quarter.
          </p>
        </Reveal>

        <Reveal y={48} className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black">
          <div
            className="tetris-card md:col-span-2 md:row-span-2 border-b md:border-b-0 md:border-r border-black p-8 md:p-12 relative overflow-hidden"
            data-testid="market-tile-1"
          >
            <img src={ARCH_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
            <div className="relative">
              <div className="label-eyebrow">YTD AVG. ETF RETURN</div>
              <div className="font-heading font-black text-[22vw] md:text-[10vw] leading-none tracking-tighter mt-4">
                <CountUp value={12.4} prefix="+" decimals={1} />
                <span className="text-[#FF4500]">%</span>
              </div>
              <p className="mt-6 max-w-md text-black/70 leading-relaxed">
                Diversified Canadian/US blended index ETFs continue to outperform actively
                managed mutual funds — at a fraction of the fee.
              </p>
            </div>
          </div>

          <div className="tetris-card border-b border-black p-8" data-testid="market-tile-2">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">BOC POLICY RATE</div>
              {market?.live && (
                <span className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-[#FF4500]">
                  <span className="w-1.5 h-1.5 bg-[#FF4500] rounded-full animate-pulse" /> Live
                </span>
              )}
            </div>
            <div className="font-heading font-black text-5xl mt-4">
              <CountUp value={policyRate} decimals={2} suffix="%" />
            </div>
            <p className="mt-3 text-sm text-black/60">
              {asOf ? `Bank of Canada · as of ${asOf}` : "Target for the overnight rate."}
            </p>
          </div>

          <div className="tetris-card p-8" data-testid="market-tile-3">
            <div className="label-eyebrow">5-YR CONVENTIONAL MORTGAGE</div>
            <div className="font-heading font-black text-5xl mt-4">
              <CountUp value={mortgageRate} decimals={2} suffix="%" />
            </div>
            <p className="mt-3 text-sm text-black/60">
              {asOf ? `Bank of Canada · as of ${asOf}` : "Posted conventional rate."}
            </p>
          </div>

          <div
            className="tetris-card md:col-span-3 border-t border-black p-8 md:p-12 grid grid-cols-1 md:grid-cols-3 gap-6"
            data-testid="market-tile-4"
          >
            <div>
              <div className="label-eyebrow">TFSA 2025 LIMIT</div>
              <div className="font-heading font-bold text-3xl mt-2">
                <CountUp value={7000} prefix="$" decimals={0} />
              </div>
            </div>
            <div>
              <div className="label-eyebrow">RRSP DEADLINE</div>
              <div className="font-heading font-bold text-3xl mt-2">MAR 03</div>
            </div>
            <div>
              <div className="label-eyebrow">INFLATION (YoY)</div>
              <div className="font-heading font-bold text-3xl mt-2">
                <CountUp value={2.1} decimals={1} suffix="%" />
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function MoneyHacks({ tips }) {
  return (
    <section id="hacks" className="border-b border-black bg-[#F5F5F5]" data-testid="hacks-section">
      <Marquee text="HACKS · TIPS · TRICKS · SAVE MORE · INVEST SMARTER" />

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-24 md:py-32">
        <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="label-eyebrow">MONEY HACKS</div>
            <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none mt-4">
              Small moves. <em className="not-italic text-[#FF4500]">Big compounding.</em>
            </h2>
          </div>
          <p className="lg:col-span-5 self-end text-black/70 max-w-md">
            A revolving library of tactics I share with clients. Steal them. Use them. Tell a
            friend.
          </p>
        </Reveal>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-black bg-white"
        >
          {tips.length === 0 && (
            <div className="p-10 col-span-full text-center text-black/40 font-mono text-xs uppercase tracking-[0.2em]">
              Loading tips…
            </div>
          )}
          {tips.map((t, idx) => {
            const Icon = ICON_MAP[t.icon] || Sparkles;
            return (
              <motion.article
                variants={staggerItem}
                key={t.id}
                className={`tetris-card group p-8 lg:p-10 border-black ${
                  idx % 3 !== 2 ? "lg:border-r" : ""
                } ${idx % 2 !== 1 ? "md:border-r lg:border-r" : ""} ${
                  idx < tips.length - 1 ? "border-b" : ""
                }`}
                data-testid={`tip-card-${idx}`}
              >
                <div className="flex items-start justify-between">
                  <Icon className="w-7 h-7 stroke-[1.5] group-hover:text-[#FF4500] transition-colors" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/50">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-heading font-semibold text-2xl mt-8 leading-tight">{t.title}</h3>
                <p className="mt-4 text-black/70 leading-relaxed">{t.content}</p>
                <div className="mt-8 pt-4 border-t border-black/10 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-black/60">
                  <span>{t.category}</span>
                  <span className="text-[#FF4500] opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-300">
                    Steal this ↗
                  </span>
                </div>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

function Articles({ articles }) {
  return (
    <section id="articles" className="border-b border-black" data-testid="articles-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-24 md:py-32">
        <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="label-eyebrow">INSIGHTS</div>
            <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none mt-4">
              Reading worth your <em className="not-italic text-[#FF4500]">coffee break.</em>
            </h2>
          </div>
        </Reveal>

        <div className="border-t border-black">
          {articles.length === 0 && (
            <div className="py-12 text-center text-black/40 font-mono text-xs uppercase tracking-[0.2em]">
              No articles yet.
            </div>
          )}
          {articles.map((a, idx) => {
            const d = new Date(a.created_at);
            const month = d.toLocaleString("en-US", { month: "short" }).toUpperCase();
            const day = String(d.getDate()).padStart(2, "0");
            const isExternal = !!a.external_url;
            const href = isExternal ? a.external_url : `/articles/${a.id}`;
            return (
              <Reveal key={a.id} delay={Math.min(idx * 0.05, 0.3)} y={20}>
                <a
                  href={href}
                  target={isExternal ? "_blank" : undefined}
                  rel={isExternal ? "noreferrer" : undefined}
                  className="article-row block border-b border-black py-8 md:py-10 cursor-pointer"
                  data-testid={`article-row-${a.id}`}
                >
                  <div className="grid grid-cols-12 gap-6 items-center">
                    <div className="col-span-3 md:col-span-2 font-mono text-sm md:text-base font-medium">
                      {month} {day}
                    </div>
                    <div className="col-span-9 md:col-span-3 font-mono text-[11px] uppercase tracking-[0.2em] text-black/60 flex items-center gap-2">
                      {a.category}
                      {isExternal && <span className="text-[#FF4500]">↗ EXTERNAL</span>}
                    </div>
                    <div className="col-span-12 md:col-span-6 font-heading font-semibold text-2xl md:text-3xl leading-tight">
                      {a.title}
                    </div>
                    <div className="col-span-12 md:col-span-1 flex md:justify-end">
                      <ArrowUpRight className="article-arrow w-6 h-6" />
                    </div>
                  </div>
                  <p className="mt-3 ml-0 md:ml-[41.66%] text-black/60 max-w-2xl">{a.excerpt}</p>
                </a>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const CHAPTERS = [
  [
    "CHAPTER 01 // INDEPENDENCE",
    "Carrier-agnostic advice",
    "Independent with Experior Financial since 2023. No proprietary quotas, no pushed bank products — just what mathematically fits your balance sheet.",
  ],
  [
    "CHAPTER 02 // ACCESSIBILITY",
    "A real person on the line",
    "In an era of AI chatbots and call-center purgatory, Northern Ontario families have a direct number they can call whenever life shifts.",
  ],
  [
    "CHAPTER 03 // EXECUTION",
    "LLQP certified & battle-tested",
    "Licensed Life Insurance Professional handling family planning, critical illness, TFSAs, RRSPs, and high-cash-value life structures.",
  ],
];

function About() {
  return (
    <section id="about" className="border-b border-black bg-[#0A0A0A] text-white" data-testid="about-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <Reveal className="border border-white/30 aspect-[4/5] overflow-hidden relative">
            <img
              src={ABOUT_IMG}
              alt="Dom Lafrance advising clients at his desk in Dryden, Ontario"
              width={1000}
              height={1250}
              loading="lazy"
              className="w-full h-full object-cover object-center grayscale hover:grayscale-0 transition-all duration-700"
              data-testid="about-image"
            />
            <div className="absolute bottom-0 left-0 border-t border-r border-white/30 bg-[#0A0A0A] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">
              DRYDEN · ON
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7 flex flex-col justify-center">
          <Reveal>
            <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-white/60">ABOUT</div>
            <h2 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-tight mt-4">
              I'm Dom. I help families <span className="text-[#FF4500]">keep more</span> of what they earn.
            </h2>
          </Reveal>

          <div className="mt-10 border-t border-white/20">
            {CHAPTERS.map(([num, title, body], i) => (
              <Reveal key={num} delay={i * 0.08} className="py-6 border-b border-white/20">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6">
                  <div className="md:col-span-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4500]">
                    {num}
                  </div>
                  <div className="md:col-span-8">
                    <h3 className="font-heading font-semibold text-xl">{title}</h3>
                    <p className="mt-2 text-white/70 leading-relaxed max-w-xl">{body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-0 border border-white/30">
            {[
              ["EST. 2023", "Experior Financial"],
              ["3+ YRS", "Hands-on advising"],
              ["LLQP", "Life insurance license"],
              ["100%", "Carrier-independent"],
            ].map(([big, small], i) => (
              <div
                key={big}
                className={`p-6 border-white/30 ${i < 3 ? "border-r" : ""} ${i < 2 ? "border-b sm:border-b-0" : ""}`}
              >
                <div className="font-heading font-black text-2xl md:text-3xl">{big}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 mt-2">{small}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Connect() {
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [newsletter, setNewsletter] = useState("");
  const [sending, setSending] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.message) {
      toast.error("Please fill in name, email, and a quick message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", contact);
      toast.success("Message received. Dom will be in touch within 24 hours.");
      setContact({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send right now.");
    } finally {
      setSending(false);
    }
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletter) return;
    try {
      await api.post("/newsletter", { email: newsletter });
      toast.success("You're in. Monthly market notes incoming.");
      setNewsletter("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't subscribe.");
    }
  };

  return (
    <section id="connect" className="border-b border-black" data-testid="connect-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <div className="label-eyebrow">LET'S CONNECT</div>
          <h2 className="font-heading font-bold text-5xl sm:text-6xl tracking-tighter leading-[0.9] mt-4">
            Ask me <span className="text-[#FF4500]">anything.</span>
          </h2>
          <p className="mt-6 text-black/70 max-w-md leading-relaxed">
            Whether it's "should I refinance?" or "what's the best TFSA strategy for $40k?" — I
            read every message and reply personally.
          </p>

          <form onSubmit={submitNewsletter} className="mt-12 border-t border-black pt-8" data-testid="newsletter-form">
            <div className="label-eyebrow mb-4">Monthly notes</div>
            <div className="flex gap-2">
              <input
                type="email"
                value={newsletter}
                onChange={(e) => setNewsletter(e.target.value)}
                placeholder="your@email.com"
                className="dom-input flex-1"
                required
                data-testid="newsletter-email-input"
              />
              <button type="submit" className="btn-primary" data-testid="newsletter-submit-btn">
                Subscribe
              </button>
            </div>
          </form>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submitContact} className="space-y-8" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="label-eyebrow">Name</label>
                <input
                  type="text"
                  value={contact.name}
                  onChange={(e) => setContact({ ...contact, name: e.target.value })}
                  className="dom-input mt-3"
                  placeholder="First & last"
                  required
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="label-eyebrow">Email</label>
                <input
                  type="email"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  className="dom-input mt-3"
                  placeholder="you@email.com"
                  required
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div>
              <label className="label-eyebrow">Phone (optional)</label>
              <input
                type="tel"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                className="dom-input mt-3"
                placeholder="(807) 555-0123"
                data-testid="contact-phone-input"
              />
            </div>
            <div>
              <label className="label-eyebrow">What's on your mind?</label>
              <textarea
                rows={4}
                value={contact.message}
                onChange={(e) => setContact({ ...contact, message: e.target.value })}
                className="dom-input mt-3 resize-none"
                placeholder="Tell me what you're working on…"
                required
                data-testid="contact-message-input"
              />
            </div>
            <button type="submit" disabled={sending} className="btn-primary w-full md:w-auto" data-testid="contact-submit-btn">
              {sending ? "Sending…" : "Send message →"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-white" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 pt-20 pb-10">
        <Reveal className="font-heading font-black tracking-tighter leading-[0.85] text-[18vw] lg:text-[14vw]">
          LET'S<br />TALK<span className="text-[#FF4500]">.</span>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 border border-black">
          <a
            href="https://instagram.com/domlafrance_entrepreneur"
            target="_blank"
            rel="noreferrer"
            className="p-6 border-b md:border-b md:border-r border-black hover:bg-[#FF4500] hover:text-white transition-colors group lg:border-b-0"
            data-testid="footer-instagram"
          >
            <Instagram className="w-6 h-6 mb-6" />
            <div className="label-eyebrow">Instagram</div>
            <div className="font-heading font-semibold text-base md:text-lg mt-2 break-all leading-tight">
              @domlafrance<wbr />_entrepreneur
            </div>
          </a>
          <a
            href="https://www.facebook.com/share/1F1tbgb2sB/?mibextid=wwXIfr"
            target="_blank"
            rel="noreferrer"
            className="p-6 border-b md:border-b lg:border-r lg:border-b-0 border-black hover:bg-[#FF4500] hover:text-white transition-colors"
            data-testid="footer-facebook"
          >
            <Facebook className="w-6 h-6 mb-6" />
            <div className="label-eyebrow">Facebook</div>
            <div className="font-heading font-semibold text-base md:text-lg mt-2 leading-tight">
              Dom the Finance Guy
            </div>
          </a>
          <a
            href="https://youtube.com/@domthefinanceguy?si=zypLKZ-lQ364Vspl"
            target="_blank"
            rel="noreferrer"
            className="p-6 border-b md:border-r lg:border-b-0 border-black hover:bg-[#FF4500] hover:text-white transition-colors"
            data-testid="footer-youtube"
          >
            <Youtube className="w-6 h-6 mb-6" />
            <div className="label-eyebrow">YouTube</div>
            <div className="font-heading font-semibold text-base md:text-lg mt-2 leading-tight">
              Dom the Finance Guy
            </div>
          </a>
          <a
            href="tel:+18077091420"
            className="p-6 border-b md:border-r lg:border-b-0 border-black hover:bg-[#FF4500] hover:text-white transition-colors"
            data-testid="footer-phone"
          >
            <Phone className="w-6 h-6 mb-6" />
            <div className="label-eyebrow">Phone</div>
            <div className="font-heading font-semibold text-base md:text-lg mt-2 leading-tight">807·709·1420</div>
          </a>
          <a
            href="mailto:dlafrance@experiorfinancial.ca"
            className="p-6 hover:bg-[#FF4500] hover:text-white transition-colors"
            data-testid="footer-email"
          >
            <Mail className="w-6 h-6 mb-6" />
            <div className="label-eyebrow">Email</div>
            <div className="font-heading font-semibold text-base md:text-lg mt-2 break-all leading-tight">
              dlafrance@experiorfinancial.ca
            </div>
          </a>
        </div>

        <div className="mt-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-black/60">
          <div>© {new Date().getFullYear()} Dom Lafrance · Experior Financial Group</div>
          <div className="flex gap-6">
            <span>Dryden · Ontario · P8N 2Y4</span>
            <Link to="/admin/login" className="hover:text-[#FF4500]" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  const [tips, setTips] = useState([]);
  const [articles, setArticles] = useState([]);
  const [market, setMarket] = useState(null);
  useSmoothScroll();

  useEffect(() => {
    (async () => {
      try {
        const [tipsRes, articlesRes, marketRes] = await Promise.all([
          api.get("/tips"),
          api.get("/articles"),
          api.get("/market"),
        ]);
        setTips(tipsRes.data || []);
        setArticles(articlesRes.data || []);
        setMarket(marketRes.data || null);
      } catch (e) {
        // silent
      }
    })();
  }, []);

  return (
    <div className="bg-white text-black" data-testid="landing-page">
      <div className="grain-overlay" aria-hidden="true" />
      <Header />
      <Hero />
      <Marquee
        phrases={[
          "MARKET INTEL",
          "MONEY-SAVING HACKS",
          "HONEST ADVICE",
          "INSURANCE & INVESTMENTS",
          "NORTHERN ONTARIO",
          "CARRIER-AGNOSTIC FIDUCIARY",
        ]}
      />
      <MarketSnapshot market={market} />
      <MoneyHacks tips={tips} />
      <Articles articles={articles} />
      <About />
      <Connect />
      <Footer />
    </div>
  );
}
